// Модальный редактор вариантов упражнения lexics для преподавателей.
// Двухшаговый процесс: ввод текста с пропусками и проверка/редактирование предложенных ответов.

import { useState, useEffect } from 'react';
import {
  Modal, Form, Button, Alert, Spinner,
} from 'react-bootstrap';
import {
  createGrammarRule,
  linkGrammarRule,
  parseLexicsText,
  suggestLexicsAnswers,
} from '../../api/exerciseApi';

// ── Main Component ─────────────────────────────────────────────────────────────

export default function ManagerLexicsVariant({
  show,
  onHide,
  onSave,
  editVariant,
  exerciseId,
}) {
  const [lexicsStep, setLexicsStep] = useState('input'); // 'input' | 'review'
  const [lexicsRawText, setLexicsRawText] = useState('');
  const [lexicsParsing, setLexicsParsing] = useState(false);
  const [lexicsParseError, setLexicsParseError] = useState('');
  const [lexicsQuestion, setLexicsQuestion] = useState('');
  const [lexicsBlanks, setLexicsBlanks] = useState({}); // "1" -> ["word1", ...]
  const [lexicsAnswers, setLexicsAnswers] = useState({}); // "1" -> selected word
  const [lexicsSaving, setLexicsSaving] = useState(false);
  const [lexicsError, setLexicsError] = useState('');
  const [lexicsSuggesting, setLexicsSuggesting] = useState(false);
  const [lexicsSuggestError, setLexicsSuggestError] = useState('');
  const [lexicsExplanations, setLexicsExplanations] = useState({});

  // ── On modal open ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!show) return;
    setLexicsParseError('');
    setLexicsError('');
    setLexicsSuggesting(false);
    setLexicsSuggestError('');

    if (editVariant) {
      const firstSentence = Object.values(editVariant.sentences || {})[0] || {};
      setLexicsQuestion(firstSentence.sentence || '');
      setLexicsBlanks(firstSentence.words || {});
      setLexicsAnswers(firstSentence.answers || {});
      setLexicsExplanations(firstSentence.explanation || {});
      setLexicsRawText('');
      setLexicsStep('review');
    } else {
      setLexicsStep('input');
      setLexicsRawText('');
      setLexicsQuestion('');
      setLexicsBlanks({});
      setLexicsAnswers({});
      setLexicsExplanations({});
    }
  }, [show, editVariant]);

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const triggerAiSuggest = async (question, blanks) => {
    setLexicsSuggesting(true);
    setLexicsSuggestError('');
    try {
      const { data } = await suggestLexicsAnswers(question, blanks);
      if (data.answers && Object.keys(data.answers).length > 0) {
        setLexicsAnswers(data.answers);
      }
      if (data.explanations && Object.keys(data.explanations).length > 0) {
        setLexicsExplanations(data.explanations);
      }
      if (data.ai_error) {
        setLexicsSuggestError(`AI: ${data.ai_error}`);
      }
    } catch {
      setLexicsSuggestError('Не удалось получить подсказку от AI');
    } finally {
      setLexicsSuggesting(false);
    }
  };

  const handleLexicsParse = async () => {
    if (!lexicsRawText.trim()) { setLexicsParseError('Введите текст'); return; }
    setLexicsParseError('');
    setLexicsParsing(true);
    try {
      const { data } = await parseLexicsText(lexicsRawText);
      setLexicsQuestion(data.question);
      setLexicsBlanks(data.blanks);
      setLexicsAnswers({});
      setLexicsStep('review');
      triggerAiSuggest(data.question, data.blanks);
    } catch (err) {
      setLexicsParseError(err?.response?.data?.detail ?? 'Ошибка при разборе текста');
    } finally {
      setLexicsParsing(false);
    }
  };

  const handleLexicsSave = async () => {
    setLexicsError('');
    const blankKeys = Object.keys(lexicsBlanks).sort((a, b) => Number(a) - Number(b));
    const hasAllAnswers = blankKeys.every((k) => lexicsAnswers[k]?.trim());
    if (!hasAllAnswers) { setLexicsError('Выберите правильное слово для каждого пропуска'); return; }

    const wordsDict = {};
    const answersDict = {};
    const explanationsDict = {};
    Object.entries(lexicsBlanks).forEach(([k, words]) => { wordsDict[k] = words; });
    Object.entries(lexicsAnswers).forEach(([k, ans]) => { answersDict[k] = ans; });
    Object.entries(lexicsExplanations).forEach(([k, exp]) => { explanationsDict[k] = exp; });

    const payload = {
      variant: null,
      sentences: {
        '1': {
          sentence: lexicsQuestion,
          words: wordsDict,
          answers: answersDict,
          explanation: explanationsDict,
        },
      },
    };
    setLexicsSaving(true);
    try {
      const savedVariant = await onSave(payload);

      // Auto-create grammar rule with explanations if AI provided them (create mode only)
      if (
        savedVariant?.id &&
        !editVariant &&
        Object.keys(lexicsExplanations).length > 0
      ) {
        try {
          const { data: rule } = await createGrammarRule({
            title: 'Пояснения к ответам',
            description: '',
            explanations: JSON.stringify(lexicsExplanations),
          });
          await linkGrammarRule(exerciseId, savedVariant.id, rule.id);
        } catch {
          // explanations are optional — don't fail the whole save
        }
      }

      onHide();
    } catch (err) {
      setLexicsError(err?.response?.data?.detail ?? 'Ошибка при сохранении');
    } finally {
      setLexicsSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
          {editVariant ? 'Редактировать вариант' : 'Новый вариант'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {lexicsStep === 'input' ? (
          <div>
            <Form.Label className="fw-medium">Текст упражнения</Form.Label>
            <Form.Text className="text-muted d-block mb-2">
              Вставьте текст в формате [TEXT] / [WORDS]
            </Form.Text>
            <Form.Control
              as="textarea"
              rows={12}
              value={lexicsRawText}
              onChange={(e) => setLexicsRawText(e.target.value)}
              placeholder={"[TEXT]\nThe city _____ very beautiful. ...\n\n[WORDS]\n[1] 1) is 2) are 3) was 4) were"}
              style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
            {lexicsParseError && (
              <Alert variant="danger" className="mt-2 py-2">{lexicsParseError}</Alert>
            )}
          </div>
        ) : (
          <div>
            {lexicsError && <Alert variant="danger" className="mb-3">{lexicsError}</Alert>}

            {/* Parsed text preview */}
            <div className="mb-4">
              <Form.Label className="fw-medium mb-1">Текст упражнения</Form.Label>
              <div style={{
                background: '#f8f9fc',
                border: '1px solid #e9ecef',
                borderRadius: '0.5rem',
                padding: '0.875rem 1rem',
                fontSize: '0.875rem',
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
              }}>
                {lexicsQuestion}
              </div>
            </div>

            {/* Correct answer per blank */}
            <Form.Label className="fw-medium mb-2">Правильные слова</Form.Label>
            {lexicsSuggesting && (
              <div className="d-flex align-items-center gap-2 mb-3" style={{ fontSize: '0.8125rem', color: '#6c757d' }}>
                <Spinner animation="border" size="sm" />
                <span>AI подбирает правильные ответы...</span>
              </div>
            )}
            {!lexicsSuggesting && lexicsSuggestError && (
              <div className="mb-2" style={{ fontSize: '0.8rem', color: '#dc3545' }}>
                {lexicsSuggestError}
              </div>
            )}
            <div className="d-flex flex-column gap-3">
              {Object.keys(lexicsBlanks).sort((a, b) => Number(a) - Number(b)).map((k) => (
                <div key={k} style={{
                  background: '#f8f9fc',
                  border: '1px solid #e9ecef',
                  borderRadius: '0.5rem',
                  padding: '0.75rem 1rem',
                }}>
                  <div className="fw-medium mb-2" style={{ fontSize: '0.875rem', color: '#1e2a4a' }}>
                    Пропуск {k}
                  </div>
                  <div className="d-flex flex-wrap gap-3">
                    {lexicsBlanks[k].map((word) => (
                      <Form.Check
                        key={word}
                        type="radio"
                        id={`lexics-blank-${k}-${word}`}
                        name={`lexics-blank-${k}`}
                        label={word}
                        checked={lexicsAnswers[k] === word}
                        onChange={() => setLexicsAnswers((prev) => ({ ...prev, [k]: word }))}
                      />
                    ))}
                  </div>
                  <Form.Group className="mt-3">
                    <Form.Label className="text-muted mb-1" style={{ fontSize: '0.8125rem' }}>
                      Пояснение (необязательно)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={lexicsExplanations[k] || ''}
                      onChange={(e) =>
                        setLexicsExplanations((prev) => ({ ...prev, [k]: e.target.value }))
                      }
                      placeholder="Объясните правило или почему этот ответ верен..."
                      style={{ fontSize: '0.875rem', resize: 'vertical' }}
                    />
                  </Form.Group>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {lexicsStep === 'input' ? (
          <>
            <Button variant="secondary" onClick={onHide}>Отмена</Button>
            <Button variant="dark" onClick={handleLexicsParse} disabled={lexicsParsing} style={{ fontWeight: 500 }}>
              {lexicsParsing ? <><Spinner size="sm" animation="border" className="me-2" />Разбираем...</> : 'Разобрать текст'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={() => setLexicsStep('input')}>
              ← Изменить текст
            </Button>
            <Button variant="secondary" onClick={onHide}>Отмена</Button>
            <Button
              variant="dark"
              onClick={handleLexicsSave}
              disabled={lexicsSaving || !Object.keys(lexicsBlanks).every((k) => lexicsAnswers[k])}
              style={{ fontWeight: 500 }}
            >
              {lexicsSaving ? <Spinner size="sm" animation="border" className="me-2" /> : null}
              Сохранить
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
